/**
 * The in-progress donation.
 *
 * Holds only what the donor has chosen. Deliberately absent: the total. The
 * total lives in the server quote, so there is nowhere in the app for a stale
 * or tampered figure to be kept and later submitted.
 */

import { create } from 'zustand';

import type {
  CreateDonationRequest,
  DonorDetails,
  Meal,
  Money,
  Quote,
  RecipientDetails,
} from '@mfa/domain';

export type PurposeCode = CreateDonationRequest['purpose'];

export interface DonationDraft {
  meal: Meal | null;
  peopleCount: number | null;
  purpose: PurposeCode | null;
  purposeDetails: string | null;
  distributionDate: string | null;
  donor: DonorDetails | null;
  /** Who the meals are for and how they get there. Never null — charity
   *  distribution is the default, not an unset state. */
  recipient: RecipientDetails;
  requiresDistributionEvidence: boolean;
  paymentMethodCode: string | null;
  /** The Easypaisa mobile account to charge, when Easypaisa is the method. */
  walletMsisdn: string | null;
  /** Client-generated once per attempt; replaying it cannot double-charge. */
  idempotencyKey: string | null;
  /** The server's authoritative price for the current selection. */
  quote: Quote | null;
}

interface DonationStore extends DonationDraft {
  setMeal: (meal: Meal) => void;
  setPeopleCount: (count: number | null) => void;
  setPurpose: (purpose: PurposeCode, details?: string | null) => void;
  setDistributionDate: (date: string) => void;
  setDonor: (donor: DonorDetails) => void;
  setRecipient: (recipient: RecipientDetails) => void;
  setRequiresEvidence: (required: boolean) => void;
  setPaymentMethod: (code: string) => void;
  setWalletMsisdn: (msisdn: string | null) => void;
  setQuote: (quote: Quote | null) => void;
  ensureIdempotencyKey: () => string;
  reset: () => void;
  /** The amount to display. Null until the server has quoted. */
  authoritativeTotal: () => Money | null;
  isReadyForReview: () => boolean;
}

/** Charity distribution with no named recipient — the overwhelming default. */
export const DEFAULT_RECIPIENT: RecipientDetails = {
  fulfilmentType: 'CHARITY_DISTRIBUTION',
  recipientName: null,
  recipientContact: null,
  instruction: null,
  address: null,
};

const EMPTY: DonationDraft = {
  meal: null,
  peopleCount: null,
  purpose: null,
  purposeDetails: null,
  distributionDate: null,
  donor: null,
  recipient: DEFAULT_RECIPIENT,
  requiresDistributionEvidence: false,
  paymentMethodCode: null,
  walletMsisdn: null,
  idempotencyKey: null,
  quote: null,
};

/** RFC 4122 v4 without pulling in a uuid dependency. */
function uuid(): string {
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const useDonationStore = create<DonationStore>((set, get) => ({
  ...EMPTY,

  setMeal: (meal) =>
    set((state) => ({
      meal,
      // Changing the meal invalidates the quote: the price per person differs,
      // so the previous total is no longer what the server would charge.
      quote: state.meal?.id === meal.id ? state.quote : null,
    })),

  setPeopleCount: (peopleCount) =>
    set((state) => ({
      peopleCount,
      quote: state.peopleCount === peopleCount ? state.quote : null,
    })),

  setPurpose: (purpose, details = null) =>
    set({ purpose, purposeDetails: details }),

  setDistributionDate: (distributionDate) => set({ distributionDate }),

  setDonor: (donor) => set({ donor }),
  setRecipient: (recipient) => set({ recipient }),
  setRequiresEvidence: (requiresDistributionEvidence) => set({ requiresDistributionEvidence }),

  setPaymentMethod: (paymentMethodCode) => set({ paymentMethodCode }),
  setWalletMsisdn: (walletMsisdn) => set({ walletMsisdn }),

  setQuote: (quote) => set({ quote }),

  ensureIdempotencyKey: () => {
    const existing = get().idempotencyKey;
    if (existing) return existing;
    const key = uuid();
    set({ idempotencyKey: key });
    return key;
  },

  reset: () => set({ ...EMPTY }),

  authoritativeTotal: () => {
    const { quote, meal, peopleCount } = get();
    if (!quote) return null;
    // Guard against showing a quote that no longer describes the selection.
    if (quote.mealId !== meal?.id || quote.peopleCount !== peopleCount) return null;
    return quote.totalAmount;
  },

  isReadyForReview: () => {
    const state = get();
    return Boolean(
      state.meal &&
        state.peopleCount &&
        state.purpose &&
        state.distributionDate &&
        state.donor &&
        state.paymentMethodCode &&
        state.authoritativeTotal(),
    );
  },
}));

/** Build the create-donation payload. Throws if the draft is incomplete. */
export function toCreateRequest(draft: DonationStore): CreateDonationRequest {
  const { meal, peopleCount, purpose, distributionDate, donor, paymentMethodCode, quote } = draft;
  if (!meal || !peopleCount || !purpose || !distributionDate || !donor || !paymentMethodCode) {
    throw new Error('Donation draft is incomplete.');
  }
  if (!quote) {
    throw new Error('A server price quote is required before creating a donation.');
  }
  return {
    quoteToken: quote.quoteToken,
    mealId: meal.id,
    peopleCount,
    purpose,
    purposeDetails: draft.purposeDetails,
    distributionDate,
    donor,
    recipient: draft.recipient,
    requiresDistributionEvidence: draft.requiresDistributionEvidence,
    paymentMethodCode,
    idempotencyKey: draft.ensureIdempotencyKey(),
  };
}
